import { isNil } from '@flowio/is';
import loadScript from '../../utilities/loadScript';
import { getApiInternalClient, getApiClient } from '../../services';
import type { Error } from '../handleAuthorizationAction';
import getAdyenLocale from './getAdyenLocale';

let checkout: Adyen.CheckoutInstance;

function challengeShopper(
  challengeToken: string,
  organization: string,
  sessionId: string,
  authKey: string,
  onComplete: (result: io.flow.v0.models.AuthorizationResult) => void,
  onFailure: (error: Error) => void,
): void {
  checkout.create('threeDS2Challenge', {
    challengeToken,
    size: '03',
    onComplete: (challengeData: Adyen.ThreeDS2ChallengeResult) => {
      const challengeResult = challengeData.data.details['threeds2.challengeResult'];
      getApiInternalClient().authorizations.postPaymentAndDetailsAndAdyenByKey({
        organization,
        headers: {
          Authorization: `Session ${sessionId}`,
        },
        key: authKey,
        body: { challenge_result: challengeResult },
      })
        .then((res) => {
          if (res.ok) {
            if (res.body.result.status === 'authorized' || res.body.result.status === 'review') {
              onComplete(res.body.result);
            } else {
              onFailure({
                name: 'ThreedsError',
                reason: res.body.result.status,
              });
            }
          } else {
            onFailure({
              name: 'FlowError',
              reason: res,
            });
          }
        });
    },
    onError: (error) => {
      onFailure({
        name: 'ThreedsError',
        reason: error,
      });
    },
  }).mount('#adyenChallenge');
}

function identifyShopper(
  data: io.flow.v0.models.AdyenIdentifyShopperData,
  organization: string,
  sessionId: string,
  authKey: string,
  onComplete: (result: io.flow.v0.models.AuthorizationResult) => void,
  onFailure: (error: Error) => void,
): void {
  checkout.create('threeDS2DeviceFingerprint', {
    fingerprintToken: data.fingerprint_token,
    onComplete: (fingerprintData: Adyen.ThreeDS2DeviceFingerprintResult) => {
      const fingerprint = fingerprintData.data.details['threeds2.fingerprint'];
      if (isNil(fingerprint) || fingerprint.length < 1) {
        onFailure({
          name: 'ThreedsError',
          reason: 'Fingerprinting failed',
        });
      }

      getApiInternalClient().authorizations.postPaymentAndDetailsAndAdyenByKey({
        organization,
        headers: {
          Authorization: `Session ${sessionId}`,
        },
        key: authKey,
        body: { fingerprint },
      })
        .then((response) => {
          if (response.ok) {
            const authResult = response.body.result.action;
            let adyenData;
            if (!isNil(authResult) && authResult.discriminator === 'authorization_result_action_native') {
              if (!isNil(authResult.details) && authResult.details.discriminator === 'adyen_native_action_details') {
                if (!isNil(authResult.details.data)) {
                  adyenData = authResult.details.data;
                }
              }
            }

            if (!isNil(adyenData) && adyenData.discriminator === 'adyen_challenge_shopper_data') {
              challengeShopper(
                adyenData.challenge_token,
                organization,
                sessionId,
                authKey,
                onComplete,
                onFailure,
              );
            } else if (response.body.result.status === 'authorized' || response.body.result.status === 'review') {
              onComplete(response.body.result);
            } else {
              onFailure({
                name: 'ThreedsError',
                reason: 'Adyen fingerprinting failed',
              });
            }
          } else {
            onFailure({
              name: 'FlowError',
              reason: response,
            });
          }
        })
        .catch((e) => {
          onFailure({
            name: 'ThreedsError',
            reason: e,
          });
        });
    },
    onError: (error) => {
      onFailure({
        name: 'ThreedsError',
        reason: error,
      });
    },
  }).mount('#adyen3ds');
}

export default function handleOgAdyenAction(
  authDetails: io.flow.v0.models.AdyenNativeActionDetails,
  mount: HTMLElement,
  sessionId: string,
  organization: string,
  authKey: string,
  onComplete: (result: io.flow.v0.models.AuthorizationResult) => void,
  onFailure: (error: any) => void,
  locale?: string,
): void {
  const adyen = document.createElement('div');
  adyen.setAttribute('id', 'adyen3ds');
  adyen.style.display = 'none';

  const challenge = document.createElement('div');
  challenge.setAttribute('id', 'adyenChallenge');
  challenge.style.height = '300px';
  challenge.style.width = '400px';

  mount.append(challenge);
  mount.appendChild(adyen);

  if (isNil(mount)) {
    onFailure({
      name: 'FlowError',
      reason: 'Invalid mount element',
    });
    return;
  }

  mount.appendChild(adyen);

  /**
   * This method is desinged to be called from outside the scope of the rest of the SDK hence any
   * `sessionId` can be passed and therefore must be requested in order to determine the environment
   * which will be used to determine the Adyen environment that we should use.
   */
  getApiClient().sessions.getBySession({
    session: sessionId,
  }).then((response) => {
    const environment = (response.ok && response.body.environment === 'sandbox') ? 'test' : 'live';
    const adyenLocale = getAdyenLocale(locale);

    loadScript('https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/3.1.0/adyen.js').then(() => {
      if (!checkout) {
        checkout = new AdyenCheckout({
          environment,
          locale: adyenLocale,
          originKey: authDetails.origin_key || '',
        });
      }

      if (isNil(authDetails.data)) {
        onFailure({
          name: 'FlowError',
          reason: 'Missing authorization details data',
        });
        return;
      }

      switch (authDetails.data.discriminator) {
        case 'adyen_identify_shopper_data':
          identifyShopper(
            authDetails.data,
            organization,
            sessionId,
            authKey,
            onComplete,
            onFailure,
          );
          break;
        case 'adyen_challenge_shopper_data':
          challengeShopper(
            authDetails.data.challenge_token,
            organization,
            sessionId,
            authKey,
            onComplete,
            onFailure,
          );
          break;
        default: break;
      }
    });
  });
}
